import { useEffect, useState } from 'react';
import type { SpreadStatus, MarketData } from '../types/trade';
import { api } from '../services/api';

interface SpreadMonitorProps {
  symbol?: string;
  refreshInterval?: number;
}

export function SpreadMonitor({
  symbol = 'fco_usdt',
  refreshInterval = 2000,
}: SpreadMonitorProps) {
  const [spread, setSpread] = useState<SpreadStatus | null>(null);
  const [market, setMarket] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
  }, [symbol, refreshInterval]);

  const loadData = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw: any = await api.getSpreadStatus(symbol);

      // Map API snake_case fields to component camelCase format
      const depth = raw.depth_2pct ? raw.depth_2pct / 2 : 0;
      const spreadData: SpreadStatus = {
        symbol: raw.symbol || symbol,
        currentSpread: raw.spread_percentage ?? raw.currentSpread ?? 0,
        targetSpread: raw.targetSpread ?? 1.0,
        isCompliant: raw.compliant ?? raw.isCompliant ?? false,
        bidDepth: raw.bidDepth ?? depth,
        askDepth: raw.askDepth ?? depth,
        lastUpdate: raw.lastUpdate || new Date().toISOString(),
      };
      setSpread(spreadData);

      // Market data is optional — the collector may not be running
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const marketRaw: any = await api.getMarketData(symbol);
        if (marketRaw && marketRaw.status !== 'error' && marketRaw.price) {
          setMarket(marketRaw);
        } else if (raw.bid && raw.ask) {
          setMarket({
            symbol,
            price: (raw.bid + raw.ask) / 2,
            change24h: 0,
            changePct24h: 0,
            high24h: 0,
            low24h: 0,
            volume24h: 0,
            timestamp: new Date().toISOString(),
          });
        }
      } catch {
        // Market data failure is non-fatal — use spread bid/ask as fallback
        if (raw.bid && raw.ask) {
          setMarket({
            symbol,
            price: (raw.bid + raw.ask) / 2,
            change24h: 0,
            changePct24h: 0,
            high24h: 0,
            low24h: 0,
            volume24h: 0,
            timestamp: new Date().toISOString(),
          });
        }
      }

      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => `$${price.toFixed(5)}`;
  const formatPercent = (pct: number) => `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
  const formatVolume = (vol: number) =>
    vol > 0 ? `$${(vol / 1000).toFixed(1)}K` : 'N/A';

  if (loading && !spread) {
    return <div className="spread-monitor loading">Loading...</div>;
  }

  if (error) {
    return <div className="spread-monitor error">{error}</div>;
  }

  return (
    <div className="spread-monitor">
      {market && (
        <div className="market-info">
          <div className="price-section">
            <span className="current-price">{formatPrice(market.price)}</span>
            {market.changePct24h !== 0 && (
              <span className={`change ${market.changePct24h >= 0 ? 'positive' : 'negative'}`}>
                {formatPercent(market.changePct24h)}
              </span>
            )}
          </div>
          {(market.high24h > 0 || market.low24h > 0 || market.volume24h > 0) && (
            <div className="market-stats">
              {market.high24h > 0 && (
                <div className="stat">
                  <span className="label">24h High</span>
                  <span className="value">{formatPrice(market.high24h)}</span>
                </div>
              )}
              {market.low24h > 0 && (
                <div className="stat">
                  <span className="label">24h Low</span>
                  <span className="value">{formatPrice(market.low24h)}</span>
                </div>
              )}
              {market.volume24h > 0 && (
                <div className="stat">
                  <span className="label">24h Volume</span>
                  <span className="value">{formatVolume(market.volume24h)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {spread && (
        <div className="spread-info">
          <div className={`spread-status ${spread.isCompliant ? 'compliant' : 'warning'}`}>
            <div className="spread-value">
              <span className="label">Spread</span>
              <span className="value">{spread.currentSpread.toFixed(2)}%</span>
              <span className="target">Target: {spread.targetSpread.toFixed(2)}%</span>
            </div>
            <div className={`compliance-badge ${spread.isCompliant ? 'ok' : 'alert'}`}>
              {spread.isCompliant ? 'COMPLIANT' : 'WARNING'}
            </div>
          </div>

          {(spread.bidDepth > 0 || spread.askDepth > 0) && (
            <div className="depth-info">
              <div className="depth-bar">
                <div
                  className="bid-depth"
                  style={{ width: `${(spread.bidDepth / (spread.bidDepth + spread.askDepth)) * 100}%` }}
                >
                  <span>Bid: ${spread.bidDepth.toFixed(0)}</span>
                </div>
                <div
                  className="ask-depth"
                  style={{ width: `${(spread.askDepth / (spread.bidDepth + spread.askDepth)) * 100}%` }}
                >
                  <span>Ask: ${spread.askDepth.toFixed(0)}</span>
                </div>
              </div>
              <div className="total-depth">
                Total Depth: ${(spread.bidDepth + spread.askDepth).toFixed(0)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
